// Which artifact kind the Cost-per-artifact section opens on.
//
// The section is scoped by a Ticket/Feature/PR toggle, and the kind it *starts* on is
// inferred: open on a kind that actually has data, because an install whose only shipped
// artifacts are Jira tickets landed on an empty "cost per shipped feature" tile with a
// populated ticket tree one toggle away.
//
// That inference can only be made from the /api/kpis payload, which arrives AFTER the
// section has already rendered — so the choice is a two-step dance, and the two bugs it
// caused are what this module exists to make testable:
//
//   * the flip was silent. `state.ca.kind` changed under a section already built for the
//     previous kind, and nothing re-rendered it, leaving Feature titles and a Feature
//     toggle around ticket-worded legends, over data fetched for the kind nobody was on
//     any more. Hence `changed` — the caller re-renders on it.
//   * the flip could happen more than once. `defaultKind` is recomputed on every repaint,
//     and a repaint follows every window and scope change — so widening the window could
//     move the toggle out from under someone who was reading it. Hence `resolved`: the
//     inference is a LANDING default, applied once per page, not a rule that keeps
//     re-deciding.
//
// Split out of kpis.ts, like router-url.ts and txFocus.ts, because kpis.ts reaches the DOM
// (and pulls in modules that install document listeners at load) and can't be imported
// under Node.

/** How many artifacts of each kind the window counted — the KPI tile's own figures. */
export interface KindCounts {
  feature: number
  ticket: number
  pr: number
}

/** The section's kind state, as much of it as this decision reads. */
export interface CaKindState {
  kind: string
  /** The user worked the toggle: their pick outranks any inference, forever. */
  userPicked: boolean
  /** The landing default has already been applied once this page. */
  kindResolved: boolean
}

/**
 * The kind to open on: the first one with data, preferring features, then tickets, then
 * PRs. All three empty falls back to `feature` — with nothing to show, the original
 * default is as good an answer as any, and it keeps the fallback stable rather than
 * ordering three zeroes.
 */
export function smartDefaultKind(c: KindCounts): string {
  if ((c.feature || 0) > 0) return 'feature'
  if ((c.ticket || 0) > 0) return 'ticket'
  if ((c.pr || 0) > 0) return 'pr'
  return 'feature'
}

/** What the section's kind becomes once `defaultKind` is known. */
export interface KindResolution {
  kind: string
  /** Carry back into state: the landing default is spent and must not fire again. */
  resolved: boolean
  /** The kind actually moved, so a rendered section is now stale and must be rebuilt. */
  changed: boolean
}

/**
 * Apply the landing default to `ca`, once.
 *
 * Two things outrank the inference, and both are "the user is already somewhere":
 * an explicit toggle (`userPicked`), and a default that has already been applied
 * (`kindResolved`) — after which the kind is sticky, so a window or scope change repaints
 * the numbers without moving the section.
 */
export function resolveKind(ca: CaKindState, defaultKind: string): KindResolution {
  if (ca.userPicked || ca.kindResolved) return { kind: ca.kind, resolved: ca.kindResolved, changed: false }
  return { kind: defaultKind, resolved: true, changed: ca.kind !== defaultKind }
}
